import { fetchConfig, debounce } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

// Horizon 4.x: sepet değişiklikleri @shopify/events CartLinesUpdateEvent ile yayınlanır;
// cart-items/cart-drawer/cart-icon event.promise çözülünce detail.sections ile güncellenir.

class MtoAutoaddProduct extends HTMLElement {
  #busy = false;
  #rerun = false;
  #abort = null;

  // Parsed tier list (primary tier from section settings + block tiers)
  #tiers = [];

  // variantId -> bool : was the auto product present at the previous evaluation
  // Used to detect manual removal (opt-out) per tier.
  #lastHadAuto = new Map();

  // All configured gift variant ids (even sold-out ones) — excluded from the
  // pre_discount_items basis so a gift can never inflate its own threshold.
  #giftVariantIds = new Set();

  connectedCallback() {
    this.#tiers = this.#readTiers();

    this.#safeEvaluate();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartSignal);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartSignal);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#abort?.abort();
  }

  get #enabled() {
    return this.dataset.enabled === 'true';
  }

  // --- Tier parsing ---------------------------------------------------------

  #bool(value, fallback = true) {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return fallback;
  }

  #normalizeTier(raw) {
    return {
      id: String(raw.id ?? ''),
      enabled: this.#bool(raw.enabled, true),
      available: this.#bool(raw.available, true),
      variantId: Number(raw.variantId || 0),
      qty: Math.max(1, Number(raw.qty || 1)),
      thresholdCents: Math.round(Number(raw.threshold || 0) * 100),
      basis: ['total_price', 'pre_discount_items'].includes(raw.basis) ? raw.basis : 'items_subtotal_price',
      minItems: Math.max(0, Number(raw.minItems || 0)),
      requiredVariantId: Number(raw.requiredVariantId || 0),
      logic: String(raw.logic || 'and').toLowerCase() === 'or' ? 'or' : 'and',
      removeWhenFalse: this.#bool(raw.removeWhenFalse, true),
    };
  }

  #readTiers() {
    const tiers = [];

    // 1) Primary tier from section-level settings (backward compatible)
    const primaryVariant = Number(this.dataset.autoVariantId || 0);
    if (primaryVariant > 0) {
      tiers.push(
        this.#normalizeTier({
          id: 'primary',
          enabled: true,
          available: this.dataset.autoAvailable,
          variantId: primaryVariant,
          qty: this.dataset.autoQuantity,
          threshold: this.dataset.threshold,
          basis: this.dataset.basis,
          minItems: this.dataset.minItems,
          requiredVariantId: this.dataset.requiredVariantId,
          logic: this.dataset.logic,
          removeWhenFalse: this.dataset.removeWhenFalse,
        })
      );
    }

    // 2) Additional tiers from section blocks (JSON payload)
    const node = this.querySelector('script[type="application/json"]');
    if (node) {
      let parsed = [];
      try {
        parsed = JSON.parse(node.textContent || '[]');
      } catch (e) {
        console.error('[MTO-Autoadd-Product] invalid tiers JSON:', e);
      }
      if (Array.isArray(parsed)) {
        for (const raw of parsed) tiers.push(this.#normalizeTier(raw));
      }
    }

    this.#giftVariantIds = new Set(tiers.filter((t) => t.variantId > 0).map((t) => t.variantId));

    // Keep only usable, enabled tiers whose product is in stock — an
    // unavailable variant would 422 on every cart event (retry storm) and
    // race against the customer's own add-to-cart requests.
    return tiers.filter((t) => {
      if (t.enabled && t.variantId > 0 && !t.available) {
        console.warn('[MTO-Autoadd-Product] tier skipped, product sold out:', t.id);
      }
      return t.enabled && t.variantId > 0 && t.available;
    });
  }

  // --- Event wiring ---------------------------------------------------------

  #onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#safeEvaluate();
  };

  // Değerlendirme sepet isteği bittikten sonra yapılır (promise çözülünce)
  #onCartSignal = (event) => {
    const done = () => this.#safeEvaluate();
    if (event?.promise) event.promise.then(done, done);
    else done();
  };

  #safeEvaluate = debounce(() => this.evaluateAndAct(), 120).bind(this);

  // --- Cart helpers ---------------------------------------------------------

  async #fetchCart(signal) {
    const res = await fetch('/cart.js', { signal, headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch /cart.js');
    return await res.json();
  }

  #getSectionsToUpdate() {
    // Horizon updates based on cart-items-component sections
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = new Set();

    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    return Array.from(sectionIds);
  }

  #cartHasVariant(cart, variantId) {
    if (!variantId || !cart?.items?.length) return false;
    return cart.items.some((it) => Number(it.variant_id) === Number(variantId) && Number(it.quantity) > 0);
  }

  // Return the stable line item key (index-independent, safe across multiple
  // removals in one cycle) for a given variant, or null.
  #findLineKeyByVariant(cart, variantId) {
    if (!variantId || !cart?.items?.length) return null;
    const item = cart.items.find(
      (it) => Number(it.variant_id) === Number(variantId) && Number(it.quantity) > 0
    );
    return item ? item.key : null;
  }

  // Sum of original_line_price (pre-discount, in subunits) over non-gift
  // lines. Mirrors how the discount campaigns measure their thresholds:
  // real products only, before any discount — items_subtotal_price is
  // post-discount, so a discounted cart could miss the gift threshold the
  // campaign itself already granted.
  #preDiscountItemsSubtotal(cart) {
    if (!cart?.items?.length) return 0;
    return cart.items.reduce((sum, it) => {
      if (this.#giftVariantIds.has(Number(it.variant_id))) return sum;
      return sum + Number(it.original_line_price || 0);
    }, 0);
  }

  #tierConditionsMet(cart, tier) {
    const checks = [];

    if (tier.thresholdCents > 0) {
      const cartValue =
        tier.basis === 'pre_discount_items'
          ? this.#preDiscountItemsSubtotal(cart)
          : Number(cart?.[tier.basis] || 0);
      checks.push(cartValue >= tier.thresholdCents);
    }

    if (tier.minItems > 0) {
      checks.push(Number(cart?.item_count || 0) >= tier.minItems);
    }

    if (tier.requiredVariantId > 0) {
      checks.push(this.#cartHasVariant(cart, tier.requiredVariantId));
    }

    if (checks.length === 0) return false;
    return tier.logic === 'or' ? checks.some(Boolean) : checks.every(Boolean);
  }

  // --- Customer opt-out (they remove an auto-added item manually) -----------

  #optoutKey(cartToken, variantId) {
    return `mto_autoadd_optout_${cartToken}_${variantId}`;
  }

  #isOptedOut(cart, variantId) {
    const token = cart?.token || 'no_token';
    return sessionStorage.getItem(this.#optoutKey(token, variantId)) === '1';
  }

  #setOptOut(cart, variantId, value) {
    const token = cart?.token || 'no_token';
    const key = this.#optoutKey(token, variantId);
    if (value) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  }

  // --- Failed-add quarantine (e.g. product sold out after page render) ------
  // Without this, a failing variant is retried on EVERY cart event: console
  // storm + concurrent POSTs racing the customer's own add-to-cart calls.

  #failKey(cartToken, variantId) {
    return `mto_autoadd_failed_${cartToken}_${variantId}`;
  }

  #isFailed(cart, variantId) {
    const token = cart?.token || 'no_token';
    return sessionStorage.getItem(this.#failKey(token, variantId)) === '1';
  }

  #setFailed(cart, variantId, value) {
    const token = cart?.token || 'no_token';
    const key = this.#failKey(token, variantId);
    if (value) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  }

  // Sepet bileşenlerinin (çekmece, sayfa, ikon) güncellenmesi için CartLinesUpdateEvent yayınlar
  #dispatchCartUpdate(action, lines, cart, sections, source = 'mto-autoadd-product') {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source, sections, didError: false },
    });
  }

  // --- Main loop ------------------------------------------------------------

  async evaluateAndAct() {
    if (!this.#enabled) return;
    if (!this.#tiers.length) return;

    if (this.#busy) {
      // A cart signal arrived mid-cycle — re-evaluate once the current cycle
      // finishes instead of silently dropping it.
      this.#rerun = true;
      return;
    }
    this.#busy = true;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    try {
      // Single snapshot per cycle. All eligible adds are collected and sent
      // as ONE batched cart/add.js call (items array); removals run after.
      // Any change re-fires cartUpdate, which schedules one more
      // reconciliation cycle (idempotent) so cross-tier effects converge.
      const cart = await this.#fetchCart(signal);

      const itemsToAdd = [];
      const keysToRemove = [];

      for (const tier of this.#tiers) {
        const met = this.#tierConditionsMet(cart, tier);
        const hasAuto = this.#cartHasVariant(cart, tier.variantId);

        // Detect manual removal while condition is still true -> opt-out
        const prevHadAuto = this.#lastHadAuto.get(tier.variantId);
        if (prevHadAuto === true && hasAuto === false && met === true) {
          this.#setOptOut(cart, tier.variantId, true);
        }
        this.#lastHadAuto.set(tier.variantId, hasAuto);

        // If conditions become false, clear opt-out and fail-quarantine so the
        // tier gets a fresh chance the next time conditions are met
        if (!met) {
          this.#setOptOut(cart, tier.variantId, false);
          this.#setFailed(cart, tier.variantId, false);
        }

        const optedOut = this.#isOptedOut(cart, tier.variantId);
        const failed = this.#isFailed(cart, tier.variantId);

        if (met && !hasAuto && !optedOut && !failed) {
          itemsToAdd.push({ id: tier.variantId, quantity: tier.qty });
        } else if (!met && tier.removeWhenFalse && hasAuto) {
          const key = this.#findLineKeyByVariant(cart, tier.variantId);
          if (key) keysToRemove.push(key);
        }
      }

      // ADD — one request per tier so a failing item (e.g. sold out between
      // page render and add) can be quarantined without sinking the others
      if (itemsToAdd.length && !signal.aborted) {
        await this.#addItems(cart, itemsToAdd, signal);
      }

      // REMOVE WHEN FALSE — change.js handles one line per call; line keys
      // are stable so earlier removals can't shift later targets
      for (const key of keysToRemove) {
        if (signal.aborted) return;
        await this.#removeLineByKey(key, signal);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') console.error('[MTO-Autoadd-Product] error:', e);
    } finally {
      this.#busy = false;
      if (this.#rerun) {
        this.#rerun = false;
        this.#safeEvaluate();
      }
    }
  }

  // Sequential adds: one cart/add.js POST per tier. A 422 (sold out, cart
  // limits, ...) quarantines that variant for the session instead of being
  // retried on every cart event; successful tiers still land. UI events are
  // dispatched once, after the last successful add.
  async #addItems(cart, items, signal) {
    const sections = this.#getSectionsToUpdate();

    let lastSuccess = null;
    let addedQty = 0;
    let firstAddedId = null;

    for (const item of items) {
      if (signal.aborted) return;

      const payload = { items: [item] };
      if (sections.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }

      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify(payload) }),
        signal,
      });

      const json = await res.json();

      if (json?.status) {
        this.#setFailed(cart, item.id, true);
        console.warn('[MTO-Autoadd-Product] add failed, variant quarantined for this session:', item.id, json);
        continue;
      }

      lastSuccess = json;
      addedQty += item.quantity;
      if (firstAddedId === null) firstAddedId = item.id;
    }

    if (!lastSuccess) return;

    // Tam sepeti bir kez çekip olaya ekle (her ürün formu kendi /cart.js isteğini atmasın)
    let updatedCart = null;
    try {
      updatedCart = await this.#fetchCart(signal);
    } catch (e) {
      if (e?.name === 'AbortError') return;
    }
    if (!updatedCart) return;

    // 'update': sepet bileşenleri yenilenir ama çekmece kendiliğinden açılmaz (hediye arka planda eklenir)
    this.#dispatchCartUpdate(
      'update',
      [{ merchandiseId: String(firstAddedId), quantity: addedQty }],
      updatedCart,
      lastSuccess.sections
    );
  }

  async #removeLineByKey(key, signal) {
    const sections = this.#getSectionsToUpdate();

    // change.js handles one line at a time; use the stable line key (id) so
    // index shifts from earlier removals in the same cycle can't hit the wrong line.
    const body = JSON.stringify({
      id: key,
      quantity: 0,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    const res = await fetch(`${Theme.routes.cart_change_url}`, {
      ...fetchConfig('json', { body }),
      signal,
    });

    const text = await res.text();
    const json = JSON.parse(text);

    if (json?.errors) {
      console.warn('[MTO-Autoadd-Product] remove failed:', json.errors);
      return;
    }

    // change.js tam sepeti döndürür; sepet bileşenleri sections ile güncellenir
    this.#dispatchCartUpdate('remove', [], json, json.sections, 'mto-autoadd-product:remove');
  }
}

if (!customElements.get('mto-autoadd-product')) {
  customElements.define('mto-autoadd-product', MtoAutoaddProduct);
}
